import type { SessionUser } from "@restlab/shared";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { ApiError, api, setCsrfToken } from "../api/client";

interface AuthContextValue {
  user: SessionUser | null;
  loading: boolean;
  login(
    email: string,
    password: string,
    rememberMe?: boolean,
    code?: string,
  ): Promise<SessionUser>;
  changePassword(newPassword: string): Promise<SessionUser>;
  deleteAccount(password: string): Promise<void>;
  logout(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void api<SessionUser>("/api/v1/auth/me")
      .then((session) => {
        setCsrfToken(session.csrfToken);
        setUser(session);
      })
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      async login(email, password, rememberMe = false, code = "") {
        const session = await api<SessionUser>("/api/v1/auth/login", {
          method: "POST",
          body: JSON.stringify({
            email,
            password,
            rememberMe,
            ...(code ? { code } : {}),
          }),
        });
        setCsrfToken(session.csrfToken);
        setUser(session);
        return session;
      },
      async changePassword(newPassword) {
        try {
          const session = await api<SessionUser>(
            "/api/v1/auth/change-password",
            {
              method: "POST",
              body: JSON.stringify({ newPassword }),
            },
          );
          setCsrfToken(session.csrfToken);
          setUser(session);
          return session;
        } catch (reason) {
          if (reason instanceof ApiError && reason.problem.status === 401) {
            setCsrfToken("");
            setUser(null);
          }
          throw reason;
        }
      },
      async deleteAccount(password) {
        await api("/api/v1/auth/account", {
          method: "DELETE",
          body: JSON.stringify({ password, confirmation: "ELIMINAR" }),
        });
        setCsrfToken("");
        setUser(null);
      },
      async logout() {
        try {
          await api("/api/v1/auth/logout", { method: "POST" });
        } catch {
          // An expired or unreachable server session must not trap the user in the authenticated shell.
        } finally {
          setCsrfToken("");
          setUser(null);
        }
      },
    }),
    [loading, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth debe usarse dentro de AuthProvider");
  return context;
}

/** Used by screens that can also be rendered in isolated previews and tests. */
export function useOptionalAuth() {
  return useContext(AuthContext);
}
